import React, { useMemo } from 'react'
import {
  Card,
  Flex,
  Title,
  Button,
  SubscriptionContainer,
  ProductPlanContainer,
  ProductName,
  BarFieldValue,
  ManageLicenseContainer,
  ManageLicense,
  SubscriptionAction,
  LicenseContainer,
  FieldLabelValueContainer,
  FieldLabel,
  FieldValue,
} from '../../components/StyledComponents'
import { Subscription } from './types'
import { PlusIcon } from './icons'
import { formatDate } from '../../utils/formatDate'

interface Props {
  subscriptions: Subscription[]
  onAddProduct?: () => void
  onAddProductLicense: (subscription: Subscription) => void
  onReduceProductLicense: (subscription: Subscription) => void
  onCancelSubscription: (subscription: Subscription) => void
}

export const SubscriptionsSection: React.FC<Props> = ({ subscriptions, onAddProduct, onAddProductLicense, onReduceProductLicense, onCancelSubscription }) => {

  //Sort subscriptions by date and push cancelled to the end
  const sortedSubscriptions = useMemo(() => {
    return [...subscriptions].sort((a, b) => {
      const aCancelled = a.subscription_status === "cancelled";
      const bCancelled = b.subscription_status === "cancelled";

      if (aCancelled && !bCancelled) return 1;
      if (!aCancelled && bCancelled) return -1;

      return (
        new Date(b.start_date).getTime() -
        new Date(a.start_date).getTime()
      );
    });
  }, [subscriptions]);

  return (
    <Card>
      <Flex>
        <Title>Your Subscriptions</Title>
        {onAddProduct ? <Button onClick={onAddProduct}>
          <PlusIcon /> Add More Product
        </Button> : null}
      </Flex>

      {sortedSubscriptions.map((s) => (
        <SubscriptionItem
          key={s.id}
          subscription={s}
          onAddProductLicense={() => onAddProductLicense(s)}
          onReduceProductLicense={() => onReduceProductLicense(s)}
          onCancelSubscription={() => onCancelSubscription(s)}
        />
      ))}

    </Card>
  )
}

const SubscriptionItem: React.FC<{ subscription: Subscription; onAddProductLicense: () => void, onReduceProductLicense: () => void, onCancelSubscription: () => void }> = ({ subscription, onAddProductLicense, onReduceProductLicense, onCancelSubscription }) => {
  const available = useMemo(() => Math.max(0, subscription.no_of_licenses - subscription.used_licenses), [subscription])
  const isCancelled = subscription.subscription_status === 'cancelled'


  return (
    <SubscriptionContainer disabled={isCancelled} >
      <ProductPlanContainer>
        <ProductName>{subscription.planPrice?.plan?.product?.product_name ?? '-'}</ProductName>
        <BarFieldValue>
          Plan: {subscription.planPrice?.plan?.plan_name ?? '-'} (Purchased on: {formatDate(subscription.start_date)})
        </BarFieldValue>
      </ProductPlanContainer>

      <ManageLicenseContainer>
        <ManageLicense>Manage Licenses:&nbsp;</ManageLicense>
        <SubscriptionAction onClick={onAddProductLicense}>&nbsp;Add&nbsp;|</SubscriptionAction>
        <SubscriptionAction onClick={onReduceProductLicense}>&nbsp;Reduce&nbsp;|</SubscriptionAction>
        <SubscriptionAction onClick={onCancelSubscription}>&nbsp;Cancel&nbsp;</SubscriptionAction>
      </ManageLicenseContainer>

      <LicenseContainer>
        <FieldLabelValueContainer>
          <FieldLabel>Total Licenses:&nbsp;</FieldLabel>
          <FieldValue>{subscription.no_of_licenses}</FieldValue>
        </FieldLabelValueContainer>

        <FieldLabelValueContainer>
          <FieldLabel>In use:&nbsp;</FieldLabel>
          <FieldValue>{subscription.used_licenses}</FieldValue>
        </FieldLabelValueContainer>

        <FieldLabelValueContainer>
          <FieldLabel>Available:&nbsp;</FieldLabel>
          <FieldValue>{available}</FieldValue>
        </FieldLabelValueContainer>
      </LicenseContainer>
    </SubscriptionContainer>
  )
}